<script setup lang="ts">
import { computed } from 'vue';
import { usePlaylistSwitchingContactSwitches } from '@/composables/api/usePlaylistSwitchingApi';
import { formatUtcDateTime } from '@/utils/date';

/**
 * The exact UTC timestamps a contact switched to a list over the report window.
 * Rendered inside the contact card's expanded section, so this component mounts
 * (and the query fires) only when the card is opened — one contact at a time.
 */
const props = defineProps<{
  locationZoneCustomPlaylistId: number;
  contactId: number;
  from: string;
  to: string;
}>();

const query = usePlaylistSwitchingContactSwitches(
  computed(() => props.locationZoneCustomPlaylistId),
  computed(() => props.contactId),
  computed(() => ({ from: props.from, to: props.to })),
);

const switches = computed(() => query.data.value?.switches ?? []);
</script>

<template>
  <!-- pl-16 = trigger p-3 (12px) + avatar w-10 (40px) + gap-3 (12px) so the
       times line up under the contact name/email above. -->
  <div class="border-border/70 border-t py-2.5 pl-16 pr-3" data-testid="playlist-contact-switch-times">
    <p v-if="query.isLoading.value" class="text-muted-foreground text-xs">Loading switch times…</p>
    <p v-else-if="query.error.value" class="text-destructive text-xs">
      Could not load switch times.
    </p>
    <ul v-else-if="switches.length > 0" class="flex flex-col gap-1.5">
      <li
        v-for="(switchedAt, index) in switches"
        :key="index"
        class="text-muted-foreground text-xs tabular-nums"
      >
        {{ formatUtcDateTime(switchedAt) }}
      </li>
    </ul>
    <p v-else class="text-muted-foreground text-xs">No recorded switch times in this range.</p>
  </div>
</template>
